/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module TechSupportListPageModule
 */

import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';

import {
    ITechSupport,
    TechSupportFsmState,
} from 'generated-types';

import { SchemaService } from 'ajs/modules/core/services/schema-service';

import { L10nService } from '@vmw/ngx-vip';
import {
    TechSupportGenerationService,
} from 'ng/modules/support/services/tech-support-generation.service';

import './recent-tech-support-list.component.less';

import * as l10n from './recent-tech-support-list.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

interface IStatusIconInfo {
    shape?: string;
    status?: string;
}

/**
 * @description Component for Tech Support to display recent tech support node.
 * @author Dnyaneshwar Jadhav
 */
@Component({
    selector: 'recent-tech-support-list',
    templateUrl: './recent-tech-support-list.component.html',
})
export class RecentTechSupportListComponent implements OnInit, OnChanges {
    @Input() public node: ITechSupport = {};

    @Input() public nodeType: string;

    public nodeName: string;

    public nodeSize: number;

    public nodeDuration: number | string;

    /**
     * Contains information regarding which cds-icon to display, depending upon the node status.
     */
    public readonly statusIconInfo: IStatusIconInfo = {};

    /**
     * Description of node status from schema.
     */
    public nodeStatusDescription: string;

    /**
     * Node status to be displayed on ui depending upon the state of the node.
     */
    public nodeStateGroupLabel: string;

    public readonly l10nKeys = l10nKeys;

    /**
     * Boolean determining if card's middle part is extended.
     */
    public showExpanded = true;

    public openViewTranscriptWizard = false;

    constructor(
        private readonly l10nService: L10nService,
        private readonly schemaService: SchemaService,
        private readonly techSupportGenerationService: TechSupportGenerationService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        if (this.node) {
            this.setNodeRelatedInfo();
        }
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.node && changes.node.currentValue) {
            this.setNodeRelatedInfo();
        }
    }

    /**
     * Map node related info like name, version etc. to local component variables.
     */
    private setNodeRelatedInfo(): void {
        this.nodeName = this.node.name;
        this.nodeSize = this.node.size;
        this.nodeDuration = this.techSupportGenerationService.formatDuration(this.node.duration || 0);

        this.setStatusIconInfo();
        this.setNodeStatusDescription();
    }

    /**
     * Depending upon the state precheck or packaging, set the icon and and status
     * to be displayed.
     */
    private setStatusIconInfo(): void {
        const {
            l10nService,
            l10nKeys,
            node,
            statusIconInfo,
        } = this;

        const stateGroup = node.state.state;

        if (
            stateGroup === TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED
        ) {
            statusIconInfo.shape = 'check-circle';
            statusIconInfo.status = 'success';

            this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.packageSuccessfulLabel);

            return;
        }

        if (stateGroup === TechSupportFsmState.TECHSUPPORT_FSM_ERROR) {
            statusIconInfo.shape = 'exclamation-circle';
            statusIconInfo.status = 'danger';

            this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.packageFailedLabel);

            return;
        }

        if (stateGroup === TechSupportFsmState.TECHSUPPORT_FSM_WARNING) {
            statusIconInfo.shape = 'exclamation-triangle';
            statusIconInfo.status = 'warning';
            this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.packageStoppedLabel);

            return;
        }

        if (stateGroup === TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED_WITH_WARNINGS) {
            statusIconInfo.shape = 'exclamation-triangle';
            statusIconInfo.status = 'warning';
            this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.packageCompletedWithWarningLabel);
        }
    }

    /**
     * Set the description from schema based on the state of node.
     */
    private setNodeStatusDescription(): void {
        const {
            node,
            schemaService,
        } = this;
        const nodeState = node?.state?.state;

        if (nodeState) {
            this.nodeStatusDescription = schemaService.getEnumValue(
                'TechSupportFsmState',
                nodeState,
            ).description;
        }
    }

    /**
     * Show/hide middle portion of card.
     */
    public toggleExpansion(): void {
        this.showExpanded = !this.showExpanded;
    }

    /**
     * Open recent tech support transcript modal.
     */
    public openTranscriptModal(): void {
        this.openViewTranscriptWizard = true;
    }

    /**
     * Update state when the wizard is closed.
     */
    public onClose(): void {
        this.openViewTranscriptWizard = false;
    }
}
